import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import CircularProgress from '@mui/material/CircularProgress'

import { apiClient } from '~/api'

// Keep local type to avoid circular imports
export type RainforestEnv = 'sandbox' | 'production'

interface LogEntry {
    timestamp: number
    connection?: string
    module?: string
    log_message?: string
}

interface Props {
    env: RainforestEnv
    logs: LogEntry[]
}

export default function Logs({ env, logs: initialLogs }: Props) {
    const [logs, setLogs] = useState<LogEntry[]>(initialLogs)
    const [removingKeys, setRemovingKeys] = useState<Set<string>>(new Set())
    const queryClient = useQueryClient()

    useEffect(() => {
        setLogs(initialLogs)
    }, [initialLogs])

    const sorted = [...logs].sort((a, b) => (b?.timestamp ?? 0) - (a?.timestamp ?? 0))

    const clearLogs = async () => {
        setLogs([])
        try {
            await apiClient.post(`integrations/rainforest/${env}/logs/clear/`).json()
            // Invalidate cache to refresh data when switching tabs
            await queryClient.invalidateQueries({ queryKey: ['settings', { name: env }] })
        } catch (err) {
            console.error('Failed to clear Rainforest logs:', err)
        }
    }

    const removeLog = async (log: LogEntry) => {
        const key = `${log.module}-${log.connection}-${log.log_message}`
        setRemovingKeys(prev => new Set(prev).add(key))
        try {
            const params = new URLSearchParams()
            params.append('module', log.module || '')
            params.append('connection', log.connection || '')
            params.append('message', log.log_message || '')
            
            await apiClient.delete(`integrations/rainforest/${env}/logs/remove/?${params.toString()}`).json()
            // Update local state to remove the log
            setLogs(prev => prev.filter(l => 
                !(l.module === log.module && 
                  l.connection === log.connection && 
                  l.log_message === log.log_message)
            ))
            // Invalidate cache to refresh data when switching tabs
            await queryClient.invalidateQueries({ queryKey: ['settings', { name: env }] })
        } catch (err) {
            console.error('Failed to remove log entry:', err)
        } finally {
            setRemovingKeys(prev => {
                const newSet = new Set(prev)
                newSet.delete(key)
                return newSet
            })
        }
    }

    const formatTs = (ts?: number) => {
        if (!ts) return '-'
        const ms = ts < 1_000_000_000_000 ? ts * 1000 : ts
        return new Date(ms).toLocaleString()
    }

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
                <Button
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={clearLogs}
                >
                    Clear All Logs
                </Button>
            </Stack>


            {!sorted.length ? (
                <Alert
                    severity="info"
                    variant="outlined"
                    sx={{ color: 'text.secondary', bgcolor: 'grey.100', borderColor: 'grey.300', '& .MuiAlert-icon': { color: 'text.secondary' } }}
                >
                    No logs available
                </Alert>
            ) : (
                <Box sx={{ mt: 1, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 1040, border: 'none' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 220, maxWidth: 220 }}>Timestamp</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 160, maxWidth: 160 }}>Connection</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 160, maxWidth: 160 }}>Module</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem' }}>Message</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', py: 0.75, px: 1, fontSize: '0.8125rem', width: 80 }}></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((entry, idx) => {
                                const key = `${entry.module}-${entry.connection}-${entry.log_message}`
                                return (
                                <TableRow key={key} sx={{ bgcolor: idx % 2 ? 'grey.50' : 'common.white' }}>
                                    <TableCell sx={{ width: 220, maxWidth: 220, p: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{formatTs(entry.timestamp)}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 160, maxWidth: 160, p: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{entry.connection ?? '-'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 160, maxWidth: 160, p: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{entry.module ?? '-'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ p: 1 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.8125rem', whiteSpace: 'pre-wrap' }}>{entry.log_message ?? '-'}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 80, p: 1 }}>
                                        <IconButton 
                                            color="inherit" 
                                            sx={{ color: 'grey.500' }} 
                                            onClick={() => removeLog(entry)} 
                                            size="small"
                                            disabled={removingKeys.has(key)}
                                        >
                                            {removingKeys.has(key) ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <DeleteIcon />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    )
}
